/**
 * Expand / collapse long collection description with a "read more" toggle.
 */
class CollectionReadMore extends HTMLElement {
  /** @type {HTMLElement | null} */
  #body = null;

  /** @type {HTMLElement | null} */
  #content = null;

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {HTMLElement | null} */
  #labelMore = null;

  /** @type {HTMLElement | null} */
  #labelLess = null;

  /** @type {number} */
  #collapsedHeight = 0;

  connectedCallback() {
    this.#body = this.querySelector('[data-body]');
    this.#content = this.querySelector('[data-content]');
    this.#toggle = this.querySelector('[data-toggle]');
    this.#labelMore = this.querySelector('[data-label-more]');
    this.#labelLess = this.querySelector('[data-label-less]');

    if (!this.#body || !this.#content || !this.#toggle) return;

    this.#measure();
    this.#toggle.addEventListener('click', this.#onToggle);
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggle);
    window.removeEventListener('resize', this.#onResize);
  }

  #onResize = () => {
    const wasExpanded = this.hasAttribute('data-expanded');
    this.#measure();
    if (wasExpanded) {
      this.setAttribute('data-expanded', '');
      if (this.#body) this.#body.style.maxHeight = `${this.#content?.scrollHeight || 0}px`;
      this.#syncLabels(true);
    }
  };

  #measure() {
    if (!this.#body || !this.#content || !this.#toggle) return;

    const lines = Number(this.dataset.collapsedLines || 4);
    const styles = getComputedStyle(this.#content);
    const lineHeight = parseFloat(styles.lineHeight) || 24;
    this.#collapsedHeight = Math.round(lineHeight * lines);

    const fullHeight = this.#content.scrollHeight;
    const needsToggle = fullHeight > this.#collapsedHeight + 8;

    if (!needsToggle) {
      this.setAttribute('data-short', '');
      this.removeAttribute('data-expanded');
      this.#body.style.maxHeight = '';
      this.#toggle.hidden = true;
      return;
    }

    this.removeAttribute('data-short');
    this.#toggle.hidden = false;

    if (this.hasAttribute('data-expanded')) {
      this.#body.style.maxHeight = `${fullHeight}px`;
      this.#syncLabels(true);
    } else {
      this.#body.style.maxHeight = `${this.#collapsedHeight}px`;
      this.#syncLabels(false);
    }
  }

  #onToggle = () => {
    if (!this.#body || !this.#content || !this.#toggle) return;

    const expanding = !this.hasAttribute('data-expanded');

    if (expanding) {
      this.setAttribute('data-expanded', '');
      this.#body.style.maxHeight = `${this.#content.scrollHeight}px`;
      this.#toggle.setAttribute('aria-expanded', 'true');
      this.#syncLabels(true);
    } else {
      this.removeAttribute('data-expanded');
      this.#body.style.maxHeight = `${this.#collapsedHeight}px`;
      this.#toggle.setAttribute('aria-expanded', 'false');
      this.#syncLabels(false);
    }
  };

  /**
   * @param {boolean} expanded
   */
  #syncLabels(expanded) {
    if (this.#labelMore) this.#labelMore.hidden = expanded;
    if (this.#labelLess) this.#labelLess.hidden = !expanded;
  }
}

if (!customElements.get('collection-read-more')) {
  customElements.define('collection-read-more', CollectionReadMore);
}
